import axios from 'axios';

/**
 * Digistore24 B2C store module.
 *
 * Handles the consumer purchase / entitlement flow that is independent of the
 * Cognito (B2B) auth. The B2C session token issued by the backend is stored
 * here (and persisted) and sent as a Bearer token on journey fetches, since
 * cross-subdomain HttpOnly cookies are not reliable for XHR.
 */

const getDefaultState = () => {
  return {
    sessionToken: null,
    sessionJourneyId: null,
    currentProduct: null,
    currentJourney: null,
    // Multi-seat: when a buyer owns more than one active purchase, the backend
    // returns a short-lived "select" token + numbered seats to choose from
    // before a real session token is issued.
    pendingSelectToken: null,
    pendingSlots: [],
  };
};

const state = getDefaultState();

const getters = {
  getSessionToken: (state) => state.sessionToken,
  isB2CAuthenticated: (state) => !!state.sessionToken,
  getCurrentProduct: (state) => state.currentProduct,
  getCurrentJourney: (state) => state.currentJourney,
  needsSlotSelection: (state) =>
    !!state.pendingSelectToken && state.pendingSlots.length > 0,
  getPendingSlots: (state) => state.pendingSlots,
};

const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  SET_SESSION(state, { token, journeyId }) {
    state.sessionToken = token;
    state.sessionJourneyId = journeyId || null;
    // A real session supersedes any pending seat selection.
    state.pendingSelectToken = null;
    state.pendingSlots = [];
  },
  CLEAR_SESSION(state) {
    state.sessionToken = null;
    state.sessionJourneyId = null;
  },
  SET_SLOT_SELECTION(state, { token, slots, journeyId }) {
    state.pendingSelectToken = token || null;
    state.pendingSlots = Array.isArray(slots) ? slots : [];
    state.sessionJourneyId = journeyId || state.sessionJourneyId;
  },
  CLEAR_SLOT_SELECTION(state) {
    state.pendingSelectToken = null;
    state.pendingSlots = [];
  },
  SET_CURRENT_PRODUCT(state, product) {
    state.currentProduct = product;
  },
  SET_CURRENT_JOURNEY(state, journey) {
    state.currentJourney = journey;
  },
};

const actions = {
  /** Public landing-page product data for a slug (scoped to the tenant). */
  async getProduct({ commit, rootState }, slug) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const tenantId =
      rootState.tenant.tenantinfo && rootState.tenant.tenantinfo.id;
    const url =
      `/v2/ds24/product/${encodeURIComponent(slug)}` +
      (tenantId ? `?tenant=${encodeURIComponent(tenantId)}` : '');
    const response = await axios.get(url);
    commit('SET_CURRENT_PRODUCT', response.data);
    return response.data;
  },

  /**
   * Verify the Digistore24 thank-you redirect signature. On success stores a
   * short-lived (15m) token that lets the buyer into the journey once without
   * OTP, and returns the resolved journey id.
   */
  async verifyThankyou({ commit, rootState }, queryParams) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const response = await axios.post('/v2/ds24/verify-thankyou', queryParams);
    if (response.data && response.data.token) {
      commit('SET_SESSION', {
        token: response.data.token,
        journeyId: response.data.journeyId,
      });
    }
    return response.data;
  },

  /** Request an OTP + magic link email for a journey. Always resolves. */
  async requestOtp({ rootState }, { email, journeyId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    // Send the current tenant origin so the magic link in the email points at
    // the right tenant + stage (e.g. https://grow.dev.commovis.com), not a
    // hardcoded default. The backend validates it against *.commovis.com.
    const appBaseUrl =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : undefined;
    const response = await axios.post('/v2/ds24/request-otp', {
      email,
      journeyId,
      appBaseUrl,
    });
    return response.data;
  },

  /**
   * Verify an OTP code. On success either stores the 8h session token, or - when
   * the buyer owns more than one active seat - stores a pending seat selection
   * (select token + numbered seats) to be resolved via selectSlot.
   */
  async verifyOtp({ dispatch, rootState }, { email, journeyId, otp }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const response = await axios.post('/v2/ds24/verify-otp', { email, journeyId, otp });
    dispatch('storeAuthResult', response.data);
    return response.data;
  },

  /**
   * Redeem a magic-link token (from the email). Same seat handling as verifyOtp.
   */
  async redeemMagicLink({ dispatch, rootState }, { magic, sid }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const response = await axios.get('/v2/ds24/magic-link', {
      params: { magic, sid },
    });
    dispatch('storeAuthResult', response.data);
    return response.data;
  },

  /**
   * Persist an auth response from verify-otp / magic-link: either a bound
   * session token, or a pending seat selection when needsSlotSelection is set.
   */
  storeAuthResult({ commit }, data) {
    if (!data || !data.token) return;
    if (data.needsSlotSelection) {
      commit('SET_SLOT_SELECTION', {
        token: data.token,
        slots: data.slots,
        journeyId: data.journeyId,
      });
    } else {
      commit('SET_SESSION', {
        token: data.token,
        journeyId: data.journeyId,
      });
    }
  },

  /**
   * Exchange a chosen seat for a seat-bound session token. Uses the pending
   * select token issued by verifyOtp / redeemMagicLink.
   */
  async selectSlot({ commit, state, rootState }, { orderId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = state.pendingSelectToken
      ? { headers: { Authorization: `Bearer ${state.pendingSelectToken}` } }
      : {};
    const response = await axios.post('/v2/ds24/select-slot', { orderId }, config);
    if (response.data && response.data.token) {
      commit('SET_SESSION', {
        token: response.data.token,
        journeyId: response.data.journeyId || state.sessionJourneyId,
      });
    }
    return response.data;
  },

  /** Fetch a journey for the authenticated B2C buyer. */
  async fetchJourney({ commit, state, rootState }, { journeyId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = state.sessionToken
      ? { headers: { Authorization: `Bearer ${state.sessionToken}` } }
      : {};
    const response = await axios.post('/v2/ds24/journey', { journeyId }, config);
    if (response.data && response.data.journey) {
      commit('SET_CURRENT_JOURNEY', response.data.journey);
    }
    return response.data ? response.data.journey : null;
  },

  /** Generate (or regenerate) the completion certificate PDF for the buyer. */
  async generateCertificate({ state, rootState }, { journeyId, displayName }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = state.sessionToken
      ? { headers: { Authorization: `Bearer ${state.sessionToken}` } }
      : {};
    const response = await axios.post(
      '/v2/ds24/journey/certificate',
      { journeyId, displayName: String(displayName || '').trim() },
      config
    );
    return response.data || null;
  },

  logout({ commit }) {
    commit('CLEAR_SESSION');
    commit('CLEAR_SLOT_SELECTION');
    commit('SET_CURRENT_JOURNEY', null);
  },

  /**
   * Admin/author: read the Digistore24 product mapping for a journey.
   * Uses the Cognito (B2B) bearer, not the B2C session token.
   */
  async getProductMapping({ rootState }, journeyId) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = { headers: { Authorization: rootState.auth.bearer } };
    const response = await axios.get(
      `/v2/ds24/product-mapping/${encodeURIComponent(journeyId)}`,
      config
    );
    return response.data ? response.data.mapping : null;
  },

  /**
   * Admin/author: create or update the Digistore24 product mapping for a
   * journey. The backend enforces global slug uniqueness and journey ownership.
   */
  async saveProductMapping({ rootState }, payload) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = { headers: { Authorization: rootState.auth.bearer } };
    const response = await axios.put('/v2/ds24/product-mapping', payload, config);
    return response.data ? response.data.mapping : null;
  },

  /**
   * Admin/author: list Digistore24 buyers (purchases) for a journey.
   * Uses the Cognito (B2B) bearer; the backend restricts this to the journey
   * author/co-author. Returns [] when there are no purchases.
   */
  async getJourneyPurchases({ rootState }, journeyId) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = { headers: { Authorization: rootState.auth.bearer } };
    const response = await axios.get(
      `/v2/ds24/purchases/${encodeURIComponent(journeyId)}`,
      config
    );
    return response.data && response.data.purchases ? response.data.purchases : [];
  },

  /**
   * Admin/author: check whether a slug is free within the tenant (ignoring the
   * journey being edited). Returns { slug, available, reason? }.
   */
  async checkSlugAvailable({ rootState }, { journeyId, slug }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = { headers: { Authorization: rootState.auth.bearer } };
    const response = await axios.get(
      `/v2/ds24/slug-available/${encodeURIComponent(journeyId)}/${encodeURIComponent(slug)}`,
      config
    );
    return response.data || { available: false };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
